import ApiClient from '@afosto/api/ApiClient';

class Oauth {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Create a new oauth client
   *
   * @param {Object} body - Create object
   * @param {Object} options - The additional request options
   * @description Returns a new client
   * @returns {Promise}
   */
  createClient(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/clients`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Delete an oauth client
   *
   * @param {string} id - Id that belongs to the oauth client
   * @param {Object} options - The additional request options
   * @description Returns the deleted client
   * @returns {Promise}
   */
  deleteClient(id, options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/clients/${id}`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Obtain a new access token
   *
   * @param {Object} body - Token request object
   * @param {Object} options - The additional request options
   * @description Returns a new access token
   * @returns {Promise}
   */
  getAccessToken(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/authorize`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Find the oauth client
   *
   * @param {string} id - Id that belongs to the oauth client
   * @param {Object} options - The additional request options
   * @description Returns the oauth client for the given client id
   * @returns {Promise}
   */
  getClient(id, options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/clients/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Obtain an authorization response
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.clientId]
   * @param {string} [query.nonce]
   * @param {string} [query.redirectUri]
   * @param {string} [query.responseType]
   * @param {string} [query.scope]
   * @param {string} [query.state]
   * @param {string} [query.tenant]
   * @param {Object} options - The additional request options
   * @description Returns a new authorization response
   * @returns {Promise}
   */
  getRedirect(query = {}, options = {}) {
    const params = {
      client_id: query.clientId,
      nonce: query.nonce,
      redirect_uri: query.redirectUri,
      response_type: query.responseType,
      scope: query.scope,
      state: query.state,
      tenant: query.tenant,
    };

    return this.apiClient.request({
      url: `/iam/oauth/authorize`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Redirect the user to the client
   *
   * @param {string} reference - Reference that belongs to the authorize request
   * @param {Object} query - The optional path parameters
   * @param {string} [query.silent] - Pass along to change the redirect response into a response with a body. Note that this option only works on previously approved authorizations.
   * @param {Object} options - The additional request options
   * @description Log the authorization, store approval and redirect the user
   * @returns {Promise}
   */
  getRedirect302(reference, query = {}, options = {}) {
    const params = {
      silent: query.silent,
    };

    return this.apiClient.request({
      url: `/iam/oauth/forward/${reference}`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get a scope and it&#39;s description
   *
   * @param {string} id - Identifier that belongs to the scope
   * @param {Object} options - The additional request options
   * @description Get scope by it&#39;s id
   * @returns {Promise}
   */
  getScope(id, options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/scopes/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * List all available scopes
   *
   * @param {Object} options - The additional request options
   * @description Get scopes for all registered services
   * @returns {Promise}
   */
  getScopesList(options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/scopes`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of clients
   *
   * @param {Object} query - The optional path parameters
   * @param {Number} [query.isDeleted]
   * @param {string} [query.scope]
   * @param {Object} options - The additional request options
   * @description Returns a list of clients
   * @returns {Promise}
   */
  listClients(query = {}, options = {}) {
    const params = {
      is_deleted: query.isDeleted,
      scope: query.scope,
    };

    return this.apiClient.request({
      url: `/iam/oauth/clients`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get a list of open / shared / third party
   *
   * @param {Object} options - The additional request options
   * @description Returns a list of clients
   * @returns {Promise}
   */
  listOpenClients(options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/apps`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Update an oauth client
   *
   * @param {string} id - Id that belongs to the oauth client
   * @param {Object} body - Update object
   * @param {Object} options - The additional request options
   * @description Returns an updated client
   * @returns {Promise}
   */
  updateClient(id, body, options = {}) {
    return this.apiClient.request({
      url: `/iam/oauth/clients/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const oauth = new Oauth();

export default Oauth;
